import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import toast from 'react-hot-toast';
import Pagination from '../components/Pagination';

const MODULES = [
  'Dashboard', 'Clients', 'Sub Agents', 'Employees', 
  'Users', 'User Groups', 'User Logs', 'Hospital Report'
];

const PERM_TYPES = ['view', 'add', 'edit', 'delete', 'backup', 'export'];

const UserGroups = () => {
  const [groups, setGroups] = useState([]);
  const [view, setView] = useState('list'); // 'list' or 'form'
  const [editingId, setEditingId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const recordsPerPage = 10;
  
  const initialPermissions = MODULES.reduce((acc, mod) => {
    acc[mod] = { view: false, add: false, edit: false, delete: false, backup: false, export: false };
    return acc;
  }, {});

  const [formData, setFormData] = useState({ 
    group_name: '', 
    status: 'Active',
    permissions: initialPermissions
  });

  useEffect(() => {
    fetchGroups();
  }, [view]);

  const fetchGroups = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/user-groups`, { withCredentials: true });
      setGroups(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load user groups');
    }
  };

  const handleEdit = (group) => {
    let perms = initialPermissions;
    if (group.permissions) {
      try {
        const parsed = JSON.parse(group.permissions);
        perms = { ...initialPermissions, ...parsed };
      } catch(e) { console.error('Error parsing permissions'); }
    }
    
    setFormData({
      group_name: group.group_name || '',
      status: group.status || 'Active',
      permissions: perms
    });
    setEditingId(group.id);
    setView('form');
  };

  const triggerDelete = (id) => {
    setDeleteModal({ isOpen: true, id });
  };

  const confirmDelete = async () => {
    const id = deleteModal.id;
    setDeleteModal({ isOpen: false, id: null });
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/user-groups/${id}`, { withCredentials: true });
      toast.success("User Group deleted successfully!");
      fetchGroups();
    } catch (err) {
      toast.error("Error deleting user group.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/user-groups/${editingId}`, formData, { withCredentials: true });
        toast.success("User Group updated successfully!");
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/user-groups`, formData, { withCredentials: true });
        toast.success("User Group created successfully!");
      }
      setView('list');
      setEditingId(null);
      setFormData({ group_name: '', status: 'Active', permissions: initialPermissions });
    } catch (err) {
      console.error(err);
      toast.error("Error saving user group");
    }
  };

  // Checkbox handlers
  const handlePermChange = (module, type, value) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [module]: {
          ...prev.permissions[module],
          [type]: value
        }
      }
    }));
  };

  const handleRowAllChange = (module, value) => {
    const newModulePerms = {};
    PERM_TYPES.forEach(pt => newModulePerms[pt] = value);
    
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [module]: newModulePerms
      }
    }));
  };

  const handleColAllChange = (type, value) => {
    setFormData(prev => {
      const newPerms = { ...prev.permissions };
      MODULES.forEach(mod => {
        newPerms[mod] = { ...newPerms[mod], [type]: value };
      });
      return { ...prev, permissions: newPerms };
    });
  };

  const handleMasterAllChange = (value) => {
    const newPerms = {};
    MODULES.forEach(mod => {
      newPerms[mod] = {};
      PERM_TYPES.forEach(pt => {
        newPerms[mod][pt] = value;
      });
    });
    setFormData(prev => ({ ...prev, permissions: newPerms }));
  };

  // Determine indeterminate/checked states for "All" checkboxes
  const isMasterChecked = MODULES.every(mod => PERM_TYPES.every(pt => formData.permissions[mod][pt]));
  const isColChecked = (type) => MODULES.every(mod => formData.permissions[mod][type]);
  const isRowChecked = (mod) => PERM_TYPES.every(pt => formData.permissions[mod][pt]);

  const filteredGroups = groups.filter(grp => 
    grp.group_name && grp.group_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredGroups.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredGroups.slice(indexOfFirstRecord, indexOfLastRecord);

  return (
    <Layout title="User Groups" subtitle="Manage roles and advanced application permissions">
      <div className="bg-slate-900/20 border border-slate-900 rounded-3xl p-6">
        
        {view === 'form' ? (
          <div className="bg-slate-900/50 p-6 rounded-2xl border border-slate-800 animate-in fade-in zoom-in duration-200">
            <h3 className="font-bold text-lg text-emerald-500 mb-6 border-b border-slate-800 pb-3">
              {editingId ? 'Edit User Group & Permissions' : 'Add New User Group'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input 
                  type="text" 
                  placeholder="Group Name (e.g., Administrator)" 
                  className="bg-slate-950 border border-slate-800 p-3 rounded-xl text-white text-sm w-full outline-none focus:border-emerald-500 transition-colors" 
                  required 
                  value={formData.group_name} 
                  onChange={e => setFormData({...formData, group_name: e.target.value})} 
                />
                <select 
                  className="bg-slate-950 border border-slate-800 p-3 rounded-xl text-white text-sm w-full outline-none focus:border-emerald-500 transition-colors appearance-none"
                  value={formData.status} 
                  onChange={e => setFormData({...formData, status: e.target.value})}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              {/* Permission Matrix */}
              <div className="mt-8 overflow-x-auto rounded-xl border border-slate-800 bg-slate-950/50">
                <table className="w-full text-left border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-slate-800 bg-slate-900">
                      <th className="p-4 font-bold text-white flex items-center gap-3">
                        <input type="checkbox" checked={isMasterChecked} onChange={e => handleMasterAllChange(e.target.checked)} className="w-4 h-4 accent-emerald-500 cursor-pointer" />
                        <span>All Modules</span>
                      </th>
                      {PERM_TYPES.map(pt => (
                        <th key={pt} className="p-4 font-bold text-gray-300 capitalize text-center">
                          <div className="flex items-center justify-center gap-2">
                            <input type="checkbox" checked={isColChecked(pt)} onChange={e => handleColAllChange(pt, e.target.checked)} className="w-4 h-4 accent-emerald-500 cursor-pointer" />
                            {pt}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/50">
                    {MODULES.map(mod => (
                      <tr key={mod} className="hover:bg-slate-800/30 transition-colors">
                        <td className="p-4 font-semibold text-emerald-400 flex items-center gap-3">
                          <input type="checkbox" checked={isRowChecked(mod)} onChange={e => handleRowAllChange(mod, e.target.checked)} className="w-4 h-4 accent-emerald-500 cursor-pointer" />
                          {mod}
                        </td>
                        {PERM_TYPES.map(pt => (
                          <td key={`${mod}-${pt}`} className="p-4 text-center">
                            <input 
                              type="checkbox" 
                              checked={formData.permissions[mod][pt]} 
                              onChange={e => handlePermChange(mod, pt, e.target.checked)} 
                              className="w-4 h-4 accent-emerald-500 cursor-pointer" 
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex gap-4 pt-6 border-t border-slate-800">
                <button type="button" onClick={() => { setView('list'); setEditingId(null); }} className="w-1/3 bg-slate-800 text-white p-3 rounded-xl font-bold hover:bg-slate-700 transition">Cancel</button>
                <button type="submit" className="w-2/3 bg-emerald-500 text-black font-bold p-3 rounded-xl hover:bg-emerald-400 transition">Save Group</button>
              </div>
            </form>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-bold text-white">Application Roles</h3>
              <div className="flex gap-3">
                <input 
                  type="text" 
                  placeholder="Search groups..." 
                  className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm outline-none focus:border-emerald-500"
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
                <button onClick={() => {
                  setFormData({ group_name: '', status: 'Active', permissions: initialPermissions });
                  setView('form');
                }} className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-xl text-sm font-bold transition">
                  + Create Group
                </button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-sm text-gray-300">
                <thead>
                  <tr className="border-b border-slate-800 text-gray-500 font-mono">
                    <th className="pb-3 w-16">S.No</th>
                    <th className="pb-3">Group Name</th>
                    <th className="pb-3">Status</th>
                    <th className="pb-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {currentRecords.map((grp, index) => (
                    <tr key={grp.id} className="hover:bg-slate-800/20">
                      <td className="py-4 text-gray-500 font-mono text-xs">{indexOfFirstRecord + index + 1}</td>
                      <td className="py-4 font-semibold text-white">{grp.group_name}</td>
                      <td className="py-4">
                        <span className={`px-2 py-1 rounded-md text-xs ${grp.status === 'Active' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-gray-500/10 text-gray-400'}`}>
                          {grp.status}
                        </span>
                      </td>
                      <td className="py-4">
                        <div className="flex justify-end gap-3">
                          <button onClick={() => handleEdit(grp)} className="text-blue-400 hover:text-blue-300 transition" title="Edit">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                          </button>
                          <button onClick={() => triggerDelete(grp.id)} className="text-rose-400 hover:text-rose-300 transition" title="Delete">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {currentRecords.length === 0 && (
                    <tr><td colSpan="4" className="text-center py-6 text-gray-500">No user groups found.</td></tr>
                  )}
                </tbody>
              </table>
              <Pagination currentPage={currentPage} totalPages={totalPages} setCurrentPage={setCurrentPage} totalItems={filteredGroups.length} />
            </div>
          </>
        )}
      </div>

      {/* Delete Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-rose-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Confirm Deletion</h3>
              <p className="text-gray-400 text-sm mb-6">Are you sure you want to delete this user group? Users assigned to this group may lose access.</p>
              <div className="flex justify-center gap-3">
                <button onClick={() => setDeleteModal({ isOpen: false, id: null })} className="px-5 py-2 text-gray-300 hover:text-white transition-colors text-sm font-semibold">Cancel</button>
                <button onClick={confirmDelete} className="px-5 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-xl font-bold text-sm transition-colors shadow-lg shadow-rose-600/20">Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default UserGroups;
